import imageCompression from 'browser-image-compression';
import { ref, uploadBytes, getDownloadURL, uploadBytesResumable, getStorage } from 'firebase/storage';
import { storage } from '../config/firebase';
import { getAuth } from 'firebase/auth';

const MAX_FILE_SIZE_MB = 10; // Increased limit for original files
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
const COMPRESSION_OPTIONS = {
  maxSizeMB: 0.5, // Target 500KB for profile images
  maxWidthOrHeight: 800, // Max 800px for profile images
  useWebWorker: true,
  fileType: 'image/jpeg' // Convert to JPEG for better compression
};

const FIREBASE_STORAGE_ERRORS = {
  'storage/unauthorized': 'Permission denied. Please check if you are signed in.',
  'storage/canceled': 'Upload was cancelled.',
  'storage/unknown': 'An unknown error occurred.',
  'storage/quota-exceeded': 'Storage quota exceeded.',
  'storage/invalid-format': 'Invalid file format.',
  'storage/object-not-found': 'File not found.',
};

export const validateImage = (file) => {
  const errors = [];

  // Check file type
  if (!ACCEPTED_TYPES.includes(file.type.toLowerCase())) {
    errors.push('Please upload a JPG, PNG, or WebP image.');
  }

  // Check file size (before compression) - more lenient for original files
  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
    errors.push(`File size must be less than ${MAX_FILE_SIZE_MB}MB.`);
  }

  return errors;
};

export const uploadProfileImage = async ({
  file,
  userId,
  onProgress = () => {},
  onError = () => {},
  onSuccess = () => {}
}) => {
  try {
    const auth = getAuth();
    
    // Ensure we have auth
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to upload images');
    }

    console.log('Starting profile image upload process...', { 
      file: { name: file.name, size: file.size, type: file.type }, 
      userId: userId || auth.currentUser.uid 
    });
    
    // Validate image
    const validationErrors = validateImage(file);
    if (validationErrors.length > 0) {
      throw new Error(validationErrors.join(' '));
    }

    // Compress image
    console.log('Compressing image...');
    const compressedFile = await imageCompression(file, COMPRESSION_OPTIONS);
    console.log('Image compressed:', {
      originalSize: file.size,
      compressedSize: compressedFile.size,
      compressionRatio: Math.round((1 - compressedFile.size / file.size) * 100) + '%'
    });
    
    // Generate filename
    const timestamp = Date.now();
    const filename = `profile_${timestamp}.jpg`; // Always use .jpg after compression
    
    // Create storage path
    const fullPath = `users/${userId || auth.currentUser.uid}/profile/${filename}`;
    console.log('Upload path:', fullPath);

    // Create storage reference
    const storageRef = ref(storage, fullPath);

    // Create metadata
    const metadata = {
      contentType: 'image/jpeg',
      cacheControl: 'public,max-age=7200',
      customMetadata: {
        userId: userId || auth.currentUser.uid,
        originalFileName: file.name,
        originalSize: file.size.toString(),
        compressedSize: compressedFile.size.toString(),
        uploadedAt: new Date().toISOString()
      }
    };

    // Start upload with metadata
    console.log('Starting upload to Firebase Storage...');
    const uploadTask = uploadBytesResumable(storageRef, compressedFile, metadata);

    return new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        // Progress handler
        (snapshot) => {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          console.log('Upload progress:', progress + '%');
          onProgress(progress);
        },
        // Error handler
        (error) => {
          console.error('Upload error:', error);
          const errorMessage = FIREBASE_STORAGE_ERRORS[error.code] || error.message;
          onError(errorMessage);
          reject(new Error(errorMessage));
        },
        // Completion handler
        async () => {
          try {
            console.log('Upload completed, getting download URL...');
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            
            const imageData = {
              url: downloadURL,
              path: uploadTask.snapshot.ref.fullPath,
              uploadedAt: new Date().toISOString(),
              originalSize: file.size,
              compressedSize: compressedFile.size
            };

            console.log('Upload successful:', imageData);
            onSuccess(downloadURL, imageData);
            resolve(imageData);
          } catch (error) {
            console.error('Error getting download URL:', error);
            onError(error.message);
            reject(error);
          }
        }
      );
    });
  } catch (error) {
    console.error('Profile image upload error:', error);
    onError(error.message);
    throw error;
  }
};

// Helper to check if a URL is a valid image
export const isValidImageUrl = async (url) => {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    const contentType = response.headers.get('content-type');
    return contentType && contentType.startsWith('image/');
  } catch {
    return false;
  }
};

// Helper to delete old profile images
export const deleteProfileImage = async (imagePath) => {
  try {
    if (!imagePath) return;
    
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to delete images');
    }

    const storageRef = ref(storage, imagePath);
    await storageRef.delete();
    console.log('Old profile image deleted:', imagePath);
  } catch (error) {
    console.error('Error deleting old profile image:', error);
    // Don't throw error for deletion failures - it's not critical
  }
};
